import { FC, useState } from 'react';
import { useAuth } from '@screentone/addon-auth-wrapper';
import { Divider, Loader, Wrapper } from '@screentone/core';

import { ConfirmationDialog } from 'components';
import { useAlert } from 'contexts/alert/useAlert';
import {
  MobileIssueDto,
  MobileIssueSectionContainer,
  useMobileIssueDtoUpdateMutation,
} from 'data/generated/graphql';
import { IssueHeader } from 'features/mobile-issue-management-edit/issue-header/IssueHeader';
import { IssueSectionsList } from 'features/mobile-issue-management-edit/issue-sections-list/IssueSectionsList';
import { useIssueSections } from './hooks/useIssueSections';
import { useIssueScheduling } from './hooks/useScheduling';
import { ScheduledFromQuery } from './hooks/useScheduling.utils';
import { IssueSectionsPreview } from './issue-sections-preview/IssueSectionsPreview';
import styles from './MobileIssueManagementEdit.module.scss';
import { deleteIssueNodeAtIndex, insertIssueNodeAtIndex } from './MobileIssueManagementEdit.utils';

const MobileIssueManagementEdit: FC = () => {
  const { currentProperty } = useAuth();
  const { alertSuccess, alertError } = useAlert();

  const { issue, setIssue, originalIssue, setOriginalIssue, updated, setUpdated, hasData, handleDragEnd } =
    useIssueSections();

  const { scheduledIssues, handleDeleteSchedule } = useIssueScheduling({ issue });

  const { mutateAsync: updateMobileIssueMutateAsync, isLoading: isUpdating } = useMobileIssueDtoUpdateMutation();

  const [tabIndex, setTabIndex] = useState(0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [sectionIndexToDelete, setSectionIndexToDelete] = useState<number | null>(null);

  const handleSave = async () => {
    if (!issue) return;
    try {
      const update = await updateMobileIssueMutateAsync({
        mobileIssueDTOUpdateInput: { issueDTO: issue as MobileIssueDto, publicationKey: currentProperty ?? '' }
      });
      setOriginalIssue(update.updateMobileIssue);
      setUpdated(false);
      setTabIndex(0);
      alertSuccess('Collection successfully saved');
    } catch (e: unknown) {
      if (e instanceof Error) {
        alertError(`Could not update the collection. ${e.message}`);
      } else {
        alertError('Could not update the collection.');
      }
    }
  };

  const handleInsertSectionFromCollection = ({
    sectionIndex,
    sectionData
  }: {
    sectionIndex: number;
    sectionData: MobileIssueSectionContainer;
  }) => {
    if (!issue) return;

    setIssue((prevIssue) => (prevIssue ? insertIssueNodeAtIndex(prevIssue, sectionIndex, sectionData) : prevIssue));

    setUpdated(true);
  };

  const handleDeleteSectionRequest = (index: number) => {
    setShowDeleteModal(true);
    setSectionIndexToDelete(index);
  };

  const deleteSection = () => {
    if (!issue || sectionIndexToDelete === null) return;

    setDeleteLoading(true);

    setIssue((prevIssue) => (prevIssue ? deleteIssueNodeAtIndex(prevIssue, sectionIndexToDelete) : prevIssue));

    setDeleteLoading(false);
    setShowDeleteModal(false);
    setSectionIndexToDelete(null);
    setUpdated(true);
  };

  const handleSetScheduledAsCurrent = (scheduled: ScheduledFromQuery) => () => {
    const issueDto = scheduled.body as MobileIssueDto;
    setIssue(issueDto);
    setUpdated(true);
    setTabIndex(0);
  };

  const handleDeleteScheduledSection = (scheduled: ScheduledFromQuery) => () => {
    // eslint-disable-next-line no-alert
    const confirmation = window.confirm('Are you sure you want to delete this scheduled section?');
    if (!confirmation) return;
    handleDeleteSchedule(scheduled.publishUtc).catch((e) => {
      alertError(
        e instanceof Error ? `Could not delete scheduled section. ${e.message}` : 'Could not delete scheduled section.'
      );
    });
  };

  return (
    <Wrapper>
      <IssueHeader
        issue={issue}
        onPublish={handleSave}
        isPublishing={isUpdating}
        disabled={isUpdating || !updated}
      />

      <Divider />

      <Wrapper margin={{ top: 'md' }}>
        <div className={styles.mobileIssueEditRow}>
          <div className={styles.mobileIssueEditDraft}>
            {!hasData && <Loader size="lg" />}

            {hasData && (
              <IssueSectionsList
                issue={issue}
                onDragEnd={handleDragEnd}
                onDeleteSection={handleDeleteSectionRequest}
                onInsertSection={handleInsertSectionFromCollection}
              />
            )}
          </div>

          <div className={styles.mobileIssueEditDivider} />

          <IssueSectionsPreview
            tabIndex={tabIndex}
            onTabChange={setTabIndex}
            handleSetScheduledAsCurrent={handleSetScheduledAsCurrent}
            handleDeleteScheduledSection={handleDeleteScheduledSection}
            issue={issue}
            originalIssue={originalIssue}
            scheduledSections={scheduledIssues}
          />
        </div>
      </Wrapper>

      {showDeleteModal && (
        <ConfirmationDialog
          title="Delete section"
          description="Are you sure you want to delete this section?"
          showDialog={setShowDeleteModal}
          onClickFunc={deleteSection}
          isLoading={deleteLoading}
        />
      )}
    </Wrapper>
  );
};

export default MobileIssueManagementEdit;
